import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/synapse/Icon';
import type { OutsideMedicine } from '@/types/patients';

/** A row being edited. Every field is a plain string until it is submitted. */
export interface OutsideMedicineRow {
  rowId: string;
  name: string;
  dose: string;
  frequency: string;
  duration: string;
}

export function newOutsideRow(from: Partial<OutsideMedicine> = {}): OutsideMedicineRow {
  return {
    rowId: crypto.randomUUID(),
    name: from.name ?? '',
    dose: from.dose ?? '',
    frequency: from.frequency ?? '',
    duration: from.duration ?? '',
  };
}

/**
 * Rows as the API takes them: a row with no name is not a medicine and is
 * dropped, and an instruction left blank is omitted rather than sent empty.
 */
export function toOutsideMedicines(rows: OutsideMedicineRow[]): OutsideMedicine[] {
  return rows
    .filter((r) => r.name.trim() !== '')
    .map((r) => {
      const out: OutsideMedicine = { name: r.name.trim() };
      if (r.dose.trim()) out.dose = r.dose.trim();
      if (r.frequency.trim()) out.frequency = r.frequency.trim();
      if (r.duration.trim()) out.duration = r.duration.trim();
      return out;
    });
}

interface OutsideMedicinesEditorProps {
  rows: OutsideMedicineRow[];
  onChange: (rows: OutsideMedicineRow[]) => void;
}

/**
 * The medicines the patient was told to buy elsewhere: typed names with their
 * instructions. Nothing here touches clinic stock — that is the other list.
 */
export function OutsideMedicinesEditor({ rows, onChange }: OutsideMedicinesEditorProps) {
  const { t } = useTranslation();

  const patch = (rowId: string, change: Partial<OutsideMedicineRow>) =>
    onChange(rows.map((r) => (r.rowId === rowId ? { ...r, ...change } : r)));

  return (
    <div className="flex flex-col gap-2.5">
      {rows.length === 0 && (
        <p className="text-[12.5px] text-muted">{t('visits.outsideEmpty')}</p>
      )}
      {rows.map((row, i) => {
        const n = i + 1;
        return (
          <div
            key={row.rowId}
            className="grid grid-cols-2 gap-2 rounded-md border border-line bg-paper-2 p-2.5 sm:grid-cols-[1.6fr_1fr_1fr_1fr_auto]"
          >
            <Input
              className="col-span-2 sm:col-span-1"
              value={row.name}
              onChange={(e) => patch(row.rowId, { name: e.target.value })}
              placeholder={t('visits.outsideName')}
              aria-label={t('visits.outsideNameN', { n })}
              maxLength={200}
            />
            <Input
              value={row.dose}
              onChange={(e) => patch(row.rowId, { dose: e.target.value })}
              placeholder={t('visits.outsideDose')}
              aria-label={t('visits.outsideDoseN', { n })}
              maxLength={100}
            />
            <Input
              value={row.frequency}
              onChange={(e) => patch(row.rowId, { frequency: e.target.value })}
              placeholder={t('visits.outsideFrequency')}
              aria-label={t('visits.outsideFrequencyN', { n })}
              maxLength={100}
            />
            <Input
              value={row.duration}
              onChange={(e) => patch(row.rowId, { duration: e.target.value })}
              placeholder={t('visits.outsideDuration')}
              aria-label={t('visits.outsideDurationN', { n })}
              maxLength={100}
            />
            <button
              type="button"
              onClick={() => onChange(rows.filter((r) => r.rowId !== row.rowId))}
              aria-label={t('visits.outsideRemoveN', { n })}
              className="flex h-10 w-10 items-center justify-center justify-self-end rounded-md border border-line bg-paper text-muted hover:text-destructive"
            >
              <Icon name="trash" size={14} />
            </button>
          </div>
        );
      })}
      <div>
        <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...rows, newOutsideRow()])}>
          <Icon name="plus" size={15} /> {t('visits.outsideAdd')}
        </Button>
      </div>
    </div>
  );
}
